// Named tape colors, contrast ink, and paint resolution.

export const DEFAULT_PALETTE: Record<string, string> = {
  RED: '#E53935', LAV: '#B39DDB', PINK: '#F48FB1', GREEN: '#43A047', BLUE: '#1E88E5',
  WHITE: '#FFFFFF', GRAY: '#9E9E9E', GREY: '#9E9E9E', BROWN: '#795548', ORANGE: '#FB8C00',
  YELLOW: '#FDD835', BLACK: '#212121', TEAL: '#00897B', TAN: '#D2B48C', PURPLE: '#8E24AA',
  MAGENTA: '#FF3EFF', SPARE: '#FFF6B0',
};

export const SPARE_FILL = '#FFF6B0';
export const SPARE_HATCH = '#F2DC5D';

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHex(s: string): boolean { return HEX.test(s); }

export function normHex(s: string): string {
  if (s.length === 4) return '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  return s.toUpperCase();
}

/** Resolve a color name or hex against the palette. Unknown names return null. */
export function toHex(v: unknown, palette: Record<string, string>): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === 'none') return null;
  if (isHex(s)) return normHex(s);
  const key = s.toUpperCase().replace(/\s+/g, ' ');
  // palette values come from projects, the account library and share links: only real hex
  // colours are used, anything else counts as no paint
  const hit = Object.prototype.hasOwnProperty.call(palette, key) ? palette[key]
    : Object.prototype.hasOwnProperty.call(palette, s) ? palette[s] : undefined;
  return typeof hit === 'string' && isHex(hit.trim()) ? normHex(hit.trim()) : null;
}

export function rgb(hex: string): [number, number, number] {
  const h = normHex(hex).slice(1);
  return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
}

/** Perceived brightness 0..1 (0.299R + 0.587G + 0.114B). */
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/** Black on light fills, white on dark. Threshold 0.55 per the label brief. */
export function ink(bg: string | null): string {
  if (!bg) return '#000000';
  return luminance(bg) > 0.55 ? '#000000' : '#FFFFFF';
}

// ---- Tape colors in resistor-code order --------------------------------------------------------
// Crews read tape like a resistor: black 0, brown 1, red 2, orange 3, yellow 4, green 5, blue 6,
// violet 7, gray 8, white 9. Lighter or related shades sit with their main color (tan with brown,
// pink with red, lavender and magenta with violet, teal with blue). A name we don't know joins the
// family its words name ("LT BLUE"), else the family its color is closest to.
export const TAPE_FAMILIES: { key: string; label: string; names: string[]; hue?: number }[] = [
  { key: 'BLACK', label: 'Black', names: ['BLACK'] },
  { key: 'BROWN', label: 'Brown', names: ['BROWN', 'TAN', 'BEIGE'], hue: 25 },
  { key: 'RED', label: 'Red', names: ['RED', 'PINK', 'MAROON', 'CRIMSON'], hue: 355 },
  { key: 'ORANGE', label: 'Orange', names: ['ORANGE', 'AMBER'], hue: 30 },
  { key: 'YELLOW', label: 'Yellow', names: ['YELLOW', 'SPARE', 'GOLD'], hue: 55 },
  { key: 'GREEN', label: 'Green', names: ['GREEN', 'LIME', 'OLIVE'], hue: 120 },
  { key: 'BLUE', label: 'Blue', names: ['BLUE', 'TEAL', 'CYAN', 'NAVY', 'SKY'], hue: 210 },
  { key: 'VIOLET', label: 'Violet', names: ['VIOLET', 'PURPLE', 'LAV', 'LAVENDER', 'MAGENTA', 'PLUM'], hue: 285 },
  { key: 'GRAY', label: 'Gray', names: ['GRAY', 'GREY', 'SILVER'] },
  { key: 'WHITE', label: 'White', names: ['WHITE', 'CLEAR'] },
];

function hsl(hex: string): [number, number, number] {
  const [r, g, b] = rgb(hex);   // already 0..1
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, d = mx - mn;
  if (!d) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [(h * 60 + 360) % 360, s, l];
}

/** Which resistor family a tape color belongs to. */
export function tapeFamily(name: string, hex?: string | null): string {
  const n = name.toUpperCase().replace(/^(LT|LIGHT|DK|DARK|PALE)\s+/, '').trim();
  for (const f of TAPE_FAMILIES) if (f.names.includes(n)) return f.key;
  const words = name.toUpperCase().split(/[^A-Z]+/);
  for (const f of TAPE_FAMILIES) if (f.names.some(w => words.includes(w))) return f.key;
  if (!hex || !isHex(hex)) return 'GRAY';
  const [h, s, l] = hsl(normHex(hex));
  if (l < 0.16) return 'BLACK';
  if (l > 0.9 && s < 0.5) return 'WHITE';
  if (s < 0.14) return 'GRAY';
  if (h >= 10 && h < 45 && l < 0.42) return 'BROWN';
  let best = 'RED', d = 999;
  for (const f of TAPE_FAMILIES) if (f.hue !== undefined && f.key !== 'BROWN') {
    const dd = Math.min(Math.abs(h - f.hue), 360 - Math.abs(h - f.hue));
    if (dd < d) { d = dd; best = f.key; }
  }
  return best;
}

/** [name, hex] pairs in resistor order, each main color first and its shades after it. */
export function tapeOrder<T extends [string, string]>(entries: T[]): { family: string; label: string; colors: T[] }[] {
  const out = TAPE_FAMILIES.map(f => ({ family: f.key, label: f.label, colors: [] as T[] }));
  for (const e of entries) out.find(o => o.family === tapeFamily(e[0], e[1]))!.colors.push(e);
  for (const o of out) {
    const f = TAPE_FAMILIES.find(x => x.key === o.family)!;
    const rank = (n: string) => { const i = f.names.indexOf(n.toUpperCase()); return i < 0 ? 99 : i; };
    o.colors.sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
  }
  return out.filter(o => o.colors.length);
}
